import React, { useImperativeHandle, useRef, useState } from 'react'
import { ethers } from 'ethers'
import { useTranslation } from 'react-i18next'

import { MiningPoolAbi, nftAbi } from '../../../../utils/config'
import { getSelectedWalletEthersProvider } from '../../../../utils/selectedWalletProvider'
import ConsoleDialog from '../../../StakingConsole/ConsoleDialog'
import { TransactionSuccessContent } from '../../../StakingConsole/TransactionSuccess'
import { mapStakingError } from '../../../StakingConsole/errorMessages'
import { waitForTransaction } from '../../../StakingConsole/transactionFeedback'
import { buildNftApprovalPlan, buildNftStakePlan, runNftStakeAfterApproval } from '../../transactionPlan'

export default function StakeModal(props) {
    const [approveInfo, setApproveInfo] = useState({})
    const [currentStep, setCurrentStep] = useState(1)
    const [approveLoading, setApproveLoading] = useState(false)
    const [stakeLoading, setStakeLoading] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const [txHash, setTxHash] = useState('')
    const approveInfoRef = useRef({})
    const { t } = useTranslation()

    const currentApproveInfo = () => approveInfoRef.current
    const initProvider = () => getSelectedWalletEthersProvider(currentApproveInfo().chainId || props.chainId)
    const handleErr = (error) => setErrorMsg(mapStakingError(error, t))

    const handleStakeLogic = async () => {
        setErrorMsg('')
        setStakeLoading(true)
        try {
            const info = currentApproveInfo()
            const contract = new ethers.Contract(info.miningContract, MiningPoolAbi, initProvider().getSigner())
            const plan = buildNftStakePlan(info.tokenId)
            const tx = await contract[plan.method](...plan.args)
            const result = await waitForTransaction(tx)
            setTxHash(result.txHash)
            setCurrentStep(3)
            props.ok?.({
                title: t('质押成功'),
                description: t('NFT 已存入矿池并开始参与奖励计算。'),
                amount: `#${info.tokenId}`,
                symbol: 'NFT',
                amountLabel: t('Token ID'),
                ...result,
            })
            return true
        } catch (error) {
            handleErr(error)
            return false
        } finally {
            setStakeLoading(false)
        }
    }

    const handleApproveLogic = async () => {
        setCurrentStep(1)
        setErrorMsg('')
        setApproveLoading(true)
        try {
            const info = currentApproveInfo()
            const nft = new ethers.Contract(info.nftAddress, nftAbi, initProvider().getSigner())
            const approved = await nft.getApproved(info.tokenId)
            if (approved.toLowerCase() !== info.miningContract.toLowerCase()) {
                const plan = buildNftApprovalPlan({
                    poolAddress: info.miningContract,
                    tokenId: info.tokenId,
                })
                const tx = await nft[plan.method](...plan.args)
                await tx.wait()
            }
            setCurrentStep(2)
            return true
        } catch (error) {
            setCurrentStep(1)
            handleErr(error)
            return false
        } finally {
            setApproveLoading(false)
        }
    }

    const handleLogic = async () => runNftStakeAfterApproval({
        approve: handleApproveLogic,
        stake: handleStakeLogic,
    })

    useImperativeHandle(props.onRef, () => ({
        setApproveInfo: (info) => new Promise((resolve) => {
            approveInfoRef.current = info
            setApproveInfo(info)
            setCurrentStep(1)
            setErrorMsg('')
            setTxHash('')
            resolve()
        }),
        handleLogic,
    }))

    const step = (number, label) => <div className={`console-step ${currentStep >= number ? 'is-active' : ''}`}><span>{number}</span><strong>{label}</strong></div>
    const action = currentStep === 1
        ? <button type="button" className="console-button console-button--primary" disabled={approveLoading} onClick={handleApproveLogic}>{approveLoading ? t('授权中...') : t('授权')}</button>
        : currentStep === 2
            ? <button type="button" className="console-button console-button--primary" disabled={stakeLoading} onClick={handleStakeLogic}>{stakeLoading ? t('质押中...') : t('质押')}</button>
            : <button type="button" className="console-button console-button--success" onClick={props.handleClose}>{t('质押完成')}</button>

    return <ConsoleDialog show={props.show} onClose={props.handleClose} closeLabel={t('关闭')} title={currentStep === 3 ? t('质押成功') : t('质押NFT')} description={currentStep === 3 ? t('交易已确认') : t('完成授权并提交质押交易')} icon={currentStep === 3 ? 'fa-check' : 'fa-cube'} tone={currentStep === 3 ? 'success' : 'default'} className={currentStep === 3 ? 'console-dialog--transaction' : ''} bodyClassName={currentStep === 3 ? 'console-dialog__body--transaction' : ''} footer={action}>
        <div className="console-stepper">{step(1, t('授权'))}<i />{step(2, t('质押NFT'))}<i />{step(3, t('完成'))}</div>
        {currentStep < 3 && <div className="console-info-note"><span>Token ID</span><strong>{approveInfo.tokenId || '-'}</strong></div>}
        {errorMsg && <p className="console-form-error" role="alert">{errorMsg}</p>}
        {currentStep === 3 && <TransactionSuccessContent title={t('质押成功')} description={t('NFT 已存入矿池并开始参与奖励计算。')} amount={`#${approveInfo.tokenId}`} symbol="NFT" amountLabel={t('Token ID')} txHash={txHash} t={t} />}
    </ConsoleDialog>
}
